import React, { useState } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { A11yWrapper } from 'components/a11y-wrapper';
import Currency from 'components/currency';
import { usePaymentCheckoutRedesignExperiment } from 'experiments/use-payment-checkout-redesign-experiment';
import { useRoute } from 'hooks/navigation/use-route';
import { geographicalFeeLabelIdMap } from 'pages/account/account-orders/constants';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { ServiceMode } from 'state/order';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { computeDeliveryFee } from 'utils/cart/helper';
import { routes } from 'utils/routing';

import { DeliveryFeesModal } from '../../delivery-fees-modal';
import { IconContainer } from '../styled';

import DeliveryFees from './delivery-fees';
import { Total, TotalLabel, TotalLabelContainer, TotalsRow } from './styled';
import { TaxesRow } from './taxes-row';

const PadLeftContainer = Box.withConfig({
  paddingLeft: '$4',
});

interface TaxesAndFeesProps {
  deliveryFee: number | null;
  deliveryFeeDiscount: number | null;
  dropoffState?: string | null;
  serviceFeeCents: number;
  smallCartFeeCents: number;
  geographicalFeeCents: number;
  taxCents: number;
  serviceMode: ServiceMode | null;
  subTotalCents: number;
  isLoading?: boolean;
}

const TaxesAndFees: React.FC<React.PropsWithChildren<TaxesAndFeesProps>> = ({
  isLoading,
  deliveryFee,
  deliveryFeeDiscount,
  dropoffState,
  serviceFeeCents,
  smallCartFeeCents,
  geographicalFeeCents,
  taxCents,
  serviceMode,
  subTotalCents,
}) => {
  const [showDeliveryFeesModal, setShowDeliveryFeesModal] = useState(false);
  const { formatMessage } = useIntl();
  const isPaymentRedesignEnabled = usePaymentCheckoutRedesignExperiment();

  const finalDeliveryFee = computeDeliveryFee({
    feeCents: deliveryFee,
    feeDiscountCents: deliveryFeeDiscount,
    serviceFeeCents,
    smallCartFeeCents,
    geographicalFeeCents,
  });

  const isFreeDelivery = finalDeliveryFee === 0 && !isLoading;
  const isSingleDeliveryFee = !serviceFeeCents && !smallCartFeeCents && !geographicalFeeCents;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  const onPressDeliveryFees = () => {
    setShowDeliveryFeesModal(true);
  };

  const totalFees = geographicalFeeCents + smallCartFeeCents + serviceFeeCents + finalDeliveryFee;
  const { pathname } = useRoute();
  const isCartPaymentRedesign = isPaymentRedesignEnabled && pathname.includes(routes.cartPayment);

  return (
    <>
      <TotalsRow mb={isCartPaymentRedesign ? '$3' : 0}>
        {isLoading ? (
          <CurrencyLineSkeleton />
        ) : isSingleDeliveryFee ? (
          <>
            <A11yWrapper>
              <TotalLabel>
                {formatMessage({
                  id: 'deliveryFee',
                })}
              </TotalLabel>
            </A11yWrapper>
            <Total testID="delivery-fee-amount">
              <Currency amount={finalDeliveryFee} textProps={{ fontSize: 'md' }} />
            </Total>
          </>
        ) : (
          <>
            {!isCartPaymentRedesign && (
              <TotalLabelContainer>
                <TotalLabel>
                  {formatMessage({
                    id: 'deliveryFees',
                  })}
                </TotalLabel>
                <IconContainer
                  accessibilityLabel={formatMessage({
                    id: 'taxesAndFeesButtonAriaLabel',
                  })}
                  onPress={onPressDeliveryFees}
                >
                  <Icon variant="info" size="$5" {...hiddenAccessibilityPlatformProps} />
                </IconContainer>
              </TotalLabelContainer>
            )}
          </>
        )}
      </TotalsRow>
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        (isCartPaymentRedesign && (
          <TotalsRow
            mb={isCartPaymentRedesign ? '$3' : 0}
            accessible
            justifyContent="space-between"
          >
            <Box flexDirection="row">
              <TotalLabel>
                {formatMessage({
                  id: 'deliveryFees',
                })}
              </TotalLabel>
              <IconContainer
                padding={0}
                accessibilityLabel={formatMessage({
                  id: 'taxesAndFeesButtonAriaLabel',
                })}
                onPress={onPressDeliveryFees}
              >
                <Icon
                  variant="info"
                  paddingLeft="$0.5"
                  size="$5"
                  {...hiddenAccessibilityPlatformProps}
                />
              </IconContainer>
            </Box>
            <Total>
              <Currency textProps={{ fontSize: 'md' }} amount={totalFees} />
            </Total>
          </TotalsRow>
        )) || (
          <PadLeftContainer>
            <TotalsRow accessible>
              <TotalLabel isFreeDelivery={isFreeDelivery}>
                {formatMessage({
                  id: 'deliveryFee',
                })}
              </TotalLabel>
              <DeliveryFees
                isFreeDelivery={isFreeDelivery}
                deliveryFee={finalDeliveryFee}
                deliveryFeeDiscount={deliveryFeeDiscount}
                isLoading={isLoading}
              />
            </TotalsRow>
            {geographicalFeeCents > 0 && (
              <TotalsRow accessible>
                <TotalLabel>
                  {formatMessage({
                    id: geographicalFeeLabelId,
                  })}
                </TotalLabel>
                <Total testID="geographical-fee-amount">
                  <Currency amount={geographicalFeeCents} textProps={{ fontSize: 'md' }} />
                </Total>
              </TotalsRow>
            )}
            {serviceFeeCents > 0 && (
              <TotalsRow accessible>
                <TotalLabel>
                  {formatMessage({
                    id: 'serviceFee',
                  })}
                </TotalLabel>
                <Total testID="service-fee-amount">
                  <Currency amount={serviceFeeCents} textProps={{ fontSize: 'md' }} />
                </Total>
              </TotalsRow>
            )}
            {smallCartFeeCents > 0 && (
              <TotalsRow accessible>
                {isLoading ? (
                  <CurrencyLineSkeleton />
                ) : (
                  <>
                    <TotalLabel>
                      {formatMessage({
                        id: 'smallCartFee',
                      })}
                    </TotalLabel>
                    <Total testID="small-cart-fee-amount">
                      <Currency amount={smallCartFeeCents} textProps={{ fontSize: 'md' }} />
                    </Total>
                  </>
                )}
              </TotalsRow>
            )}
          </PadLeftContainer>
        )
      )}
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <TaxesRow
          textProps={{ fontSize: 'md' }}
          mb={isCartPaymentRedesign ? '$3' : 0}
          taxCents={taxCents}
          isLoading={isLoading}
        />
      )}
      <DeliveryFeesModal
        open={showDeliveryFeesModal}
        onClose={() => setShowDeliveryFeesModal(false)}
        serviceMode={serviceMode}
        subTotalCents={subTotalCents}
        deliveryFeeCents={finalDeliveryFee}
        serviceFeeCents={serviceFeeCents}
        geographicalFeeCents={geographicalFeeCents}
        smallCartFeeCents={smallCartFeeCents}
        isLoading={isLoading}
      />
    </>
  );
};

export default TaxesAndFees;
